import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GitMerge, Loader2, Search } from 'lucide-react';
import { ServiceDeskAPI, serviceDeskFormilyTicketGridQueryKey } from '@sd/services/APIs';
import { extractPayloadArray } from '@sd/lib/userDisplayMap';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { TablePager } from '@sd/components/common/TablePager';

type ApiTicket = {
  id: string;
  ui_id?: string;
  subject?: string;
  status?: string;
  priority?: string;
  category?: string;
  requester?: string;
  requester_name?: string;
  tickettype?: string;
  assigned_id?: string | null;
};

const priorityColors: Record<string, string> = {
  'P1': 'bg-red-500 text-white',
  'P1 - Critical': 'bg-red-500 text-white',
  'P2': 'bg-orange-400 text-white',
  'P2 - High': 'bg-orange-400 text-white',
  'P3': 'bg-blue-400 text-white',
  'P3 - Medium': 'bg-blue-400 text-white',
  'P4': 'bg-gray-400 text-white',
};

function priorityShort(p: string): string {
  if (p.startsWith('P1')) return 'P1';
  if (p.startsWith('P2')) return 'P2';
  if (p.startsWith('P3')) return 'P3';
  if (p.startsWith('P4')) return 'P4';
  return p;
}

const isUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v);

export function AdminMergeTickets() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<string[]>([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [merging, setMerging] = useState(false);

  // Debounce search → reset page to 1
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when pageSize changes
  useEffect(() => { setPage(1); }, [pageSize]);

  const { data: apiData, isLoading, isError } = useQuery({
    queryKey: ['merge-tickets-list', debouncedSearch, page, pageSize],
    queryFn: async () => {
      const params: Record<string, unknown> = { page, page_size: pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await ServiceDeskAPI.mergeTickets.list(params);
      return res.data as unknown;
    },
    staleTime: 30_000,
    refetchOnMount: 'always',
    retry: 1,
  });

  const raw = (apiData ?? {}) as Record<string, unknown>;
  const tickets = extractPayloadArray(apiData) as ApiTicket[];
  const totalCount: number =
    (raw.count as number) ??
    (raw.total as number) ??
    (raw.total_count as number) ??
    tickets.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const toggle = (id: string) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const doMerge = async () => {
    if (selected.length < 2) { toast.error('Select at least 2 tickets'); return; }
    setMerging(true);
    try {
      await ServiceDeskAPI.mergeTickets.merge(selected);
      toast.success(`${selected.length} tickets merged successfully.`);
      setSelected([]);
      setMergeOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['merge-tickets-list'], exact: false });
      void queryClient.invalidateQueries({ queryKey: serviceDeskFormilyTicketGridQueryKey });
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message ?? e?.message ?? 'Something went wrong';
      toast.error('Merge failed', { description: msg });
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="min-w-0 p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold text-foreground">Open &amp; Pending Tickets</h1>
        <Button
          disabled={selected.length < 2}
          onClick={() => setMergeOpen(true)}
          className="w-full shrink-0 gap-2 sm:w-auto"
        >
          <GitMerge className="h-4 w-4" />
          Merge Selected ({selected.length})
        </Button>
      </div>

      {/* Search */}
      <div className="relative w-full max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search by subject or ticket ID (e.g. TXT-10049)…"
          className={cn('h-9 pl-9', search ? 'pr-16' : 'pr-3')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <Button
            type="button" variant="ghost" size="sm"
            className="absolute right-1 top-1/2 h-7 -translate-y-1/2 px-2 text-xs text-muted-foreground"
            onClick={() => setSearch('')}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading tickets…</span>
        </div>
      )}

      {/* Error */}
      {isError && (
        <p className="py-8 text-center text-sm text-destructive">
          Failed to load tickets.
        </p>
      )}

      {/* Empty */}
      {!isLoading && !isError && tickets.length === 0 && (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {search ? 'No tickets match your search.' : 'No tickets available.'}
        </p>
      )}

      {/* List */}
      {!isLoading && !isError && tickets.length > 0 && (
        <div className="rounded-lg border bg-card">
          {tickets.map((t, idx) => {
            const uiId     = t.ui_id ?? t.id;
            const subject  = t.subject ?? '—';
            const priority = t.priority ?? '';
            const pShort   = priorityShort(priority);
            const rawReq   = t.requester_name ?? t.requester ?? '';
            const requester = rawReq && !isUuid(rawReq) ? rawReq : null;
            const isChecked = selected.includes(t.id);

            return (
              <div
                key={t.id}
                onClick={() => toggle(t.id)}
                className={cn(
                  "flex items-center gap-3 px-3 py-3 cursor-pointer select-none transition-colors sm:gap-4 sm:px-5 sm:py-3.5",
                  idx !== 0 && "border-t border-dashed border-border",
                  isChecked && "bg-primary/5"
                )}
              >
                {/* Checkbox */}
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => toggle(t.id)}
                  onClick={e => e.stopPropagation()}
                  className="flex-shrink-0"
                />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-0.5">
                    <span className="font-mono text-xs text-muted-foreground font-medium">{uiId}</span>
                    {pShort && (
                      <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full", priorityColors[priority] ?? 'bg-gray-400 text-white')}>
                        {pShort}
                      </span>
                    )}
                    {t.category && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-border bg-muted/60 text-muted-foreground capitalize">
                        {t.category}
                      </span>
                    )}
                  </div>
                  <p
                    className="line-clamp-2 break-words text-sm font-semibold leading-snug text-foreground"
                    title={subject !== '—' ? subject : undefined}
                  >
                    {subject}
                  </p>
                  {requester && (
                    <p className="text-xs text-muted-foreground mt-0.5">{requester}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !isError && totalCount > 0 && (
        <div className="overflow-x-auto">
          <TablePager
            pageNum={page}
            totalPages={totalPages}
            pageSize={pageSize}
            isLoading={isLoading}
            onPageChange={setPage}
            onPageSizeChange={size => { setPageSize(size); setPage(1); }}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      )}

      {/* Merge confirm dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="flex max-h-[min(90vh,520px)] w-[calc(100%-2rem)] max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
          <DialogHeader className="shrink-0 space-y-1 border-b px-4 py-4 pr-12 text-left sm:px-6">
            <DialogTitle>Merge {selected.length} Tickets</DialogTitle>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <p className="text-sm text-muted-foreground">
              These tickets will be combined into a single ticket. The first selected ticket becomes the parent.
            </p>
            <div className="mt-3 max-h-52 space-y-2 overflow-y-auto overflow-x-hidden rounded-lg border bg-muted/30 p-3">
              {selected.map((id, i) => {
                const t = tickets.find(x => x.id === id);
                return (
                  <div key={id} className="flex min-w-0 items-start gap-2 text-sm">
                    <span className="mt-0.5 shrink-0 text-muted-foreground">{i === 0 ? '⭐' : '↳'}</span>
                    <span className="shrink-0 font-mono text-xs font-semibold text-primary">
                      {t?.ui_id ?? id}
                    </span>
                    <span className="min-w-0 flex-1 break-words text-foreground leading-snug">
                      {t?.subject ?? '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter className="shrink-0 flex-col gap-2 border-t bg-background px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
            <Button variant="outline" onClick={() => setMergeOpen(false)} disabled={merging} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={() => void doMerge()} disabled={merging} className="w-full gap-2 sm:w-auto">
              {merging
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Merging…</>
                : <><GitMerge className="h-4 w-4" /> Merge Tickets</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
